import _classCallCheck from 'babel-runtime/helpers/classCallCheck';
import _createClass from 'babel-runtime/helpers/createClass';
import _possibleConstructorReturn from 'babel-runtime/helpers/possibleConstructorReturn';
import _inherits from 'babel-runtime/helpers/inherits';
import { EventEmitter } from 'fbemitter';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { addStoppablePlayer, getStoppablePlayers, registerStoppableMedia, removeStoppablePlayer, stopAllMedia } from '../utils/media-stopping-utils';
import { Ticker } from '../utils/Ticker';

//***************************************************************************************
export var SREPlayer = function (_Component) {
  _inherits(SREPlayer, _Component);

  SREPlayer.stopAll = function stopAll() {
    var players = getStoppablePlayers(SREPlayer);
    players.forEach(function (instance) {
      instance.pause();
    });
  };

  function SREPlayer(props) {
    _classCallCheck(this, SREPlayer);

    var _this = _possibleConstructorReturn(this, _Component.call(this, props));

    _this.setTimeAsFraction = function (percentagePosition) {
      _this.srePlaybackSeekStartHandler();
      _this.seekedToTime = Math.ceil(_this.props.totalTime * percentagePosition);
      _this.srePlaybackSeekedHandler();
    };

    _this.play = function () {
      stopAllMedia();
      var startTime = _this.position >= _this.duration ? 0 : _this.position;
      var msStartTime = Math.round(startTime * 1000);
      _this.props.start(msStartTime);
    };

    _this.pause = function () {
      _this.props.stop();
    };

    _this.srePlaybackPlayHandler = function () {
      _this.ticker.startTicking();
      _this.emitter.emit('onSREPlaybackPlay');
    };

    _this.srePlaybackPauseHandler = function () {
      _this.ticker.stopTicking();
      _this.emitter.emit('onSREPlaybackPause');
    };

    _this.srePlaybackEndHandler = function () {
      _this.ticker.stopTicking();
      _this.emitter.emit('onSREPlaybackEnd');
    };

    _this.srePlaybackSeekStartHandler = function () {
      _this.emitter.emit('onSREPlaybackSeekStart');
    };

    _this.srePlaybackSeekedHandler = function () {
      _this.emitter.emit('onSREPlaybackSeeked');
    };

    _this.srePlaybackReadyHandler = function () {
      _this.sentLoaded = true;
      _this.emitter.emit('onSREPlaybackLoaded');
    };

    _this.emitter = new EventEmitter();
    _this.ticker = new Ticker();
    _this.seekedToTime = -1;
    _this.sentLoaded = false;
    return _this;
  }

  SREPlayer.prototype.componentDidMount = function componentDidMount() {
    addStoppablePlayer(this);
    if (!this.sentLoaded && this.props.ready) {
      this.srePlaybackReadyHandler();
    }
  };

  SREPlayer.prototype.componentWillUnmount = function componentWillUnmount() {
    removeStoppablePlayer(this);
  };

  SREPlayer.prototype.componentDidUpdate = function componentDidUpdate(prevProps, prevState, snapshot) {
    var prevCompleted = prevProps.completed,
        prevPlaying = prevProps.playing,
        prevCurrentTime = prevProps.currentTime;
    var _props = this.props,
        currentTime = _props.currentTime,
        playing = _props.playing,
        completed = _props.completed;

    // Simulating SRE player events
    // started playing

    if (playing && !prevPlaying) {
      this.srePlaybackPlayHandler();
    }
    // at end
    if (completed && !prevCompleted) {
      this.srePlaybackEndHandler();
    }
    // paused
    if (!prevCompleted && prevPlaying && !playing) {
      this.srePlaybackPauseHandler();
    }
    // ready aka (for here) loaded
    if (!this.sentLoaded && this.props.ready) {
      this.srePlaybackReadyHandler();
    }

    // the SRE interface only updates its currentTime when it plays so
    //  we keep track of any seeks with this.seekedToTime
    //  so this clause means: if the SRE player now knows where it is
    //   then we should stop paying attention to this.seekedToTime
    if (this.seekedToTime !== -1 && prevCurrentTime !== currentTime) {
      this.seekedToTime = -1;
    }
  };

  /**
   * ready() is SRE-specific behavior.
   * Should not be able to play anything from SRE if there isn't
   * a valid sre session. SRE player adapters should properly
   * configure when SREPlayer can be played.
   */


  SREPlayer.prototype.onPlay = function onPlay(handler) {
    return this.emitter.addListener('onSREPlaybackPlay', handler);
  };

  SREPlayer.prototype.onPause = function onPause(handler) {
    return this.emitter.addListener('onSREPlaybackPause', handler);
  };

  SREPlayer.prototype.onEnd = function onEnd(handler) {
    return this.emitter.addListener('onSREPlaybackEnd', handler);
  };

  SREPlayer.prototype.onSeekStart = function onSeekStart(handler) {
    return this.emitter.addListener('onSREPlaybackSeekStart', handler);
  };

  SREPlayer.prototype.onSeeked = function onSeeked(handler) {
    return this.emitter.addListener('onSREPlaybackSeeked', handler);
  };

  SREPlayer.prototype.onLoaded = function onLoaded(handler) {
    return this.emitter.addListener('onSREPlaybackLoaded', handler);
  };

  SREPlayer.prototype.onTick = function onTick(handler) {
    return this.ticker.onTick(handler);
  };

  // public interface end

  SREPlayer.prototype.render = function render() {
    return this.props.children(this);
  };

  _createClass(SREPlayer, [{
    key: 'ready',
    get: function get() {
      return this.props.ready;
    }
  }, {
    key: 'isLoaded',
    get: function get() {
      return this.sentLoaded;
    }
  }, {
    key: 'isPlaying',
    get: function get() {
      return this.props.playing;
    }

    // SRE player uses milliseconds but other players use seconds, so adjust for that

  }, {
    key: 'position',
    get: function get() {
      var msPosition = this.seekedToTime === -1 ? this.props.currentTime : this.seekedToTime;
      return msPosition / 1000;
    }

    // SRE player uses milliseconds but other players use seconds, so adjust for that

  }, {
    key: 'duration',
    get: function get() {
      return this.props.totalTime / 1000;
    }
  }, {
    key: 'progress',
    get: function get() {
      if (isNaN(this.duration) || this.duration === 0) {
        return 0;
      }
      var fractionRemaining = Math.min((this.duration - this.position) / this.duration, 1);
      fractionRemaining = Math.max(fractionRemaining, 0);
      return 1 - fractionRemaining;
    }
  }]);

  return SREPlayer;
}(Component);

SREPlayer.propTypes = {
  currentTime: PropTypes.number,
  totalTime: PropTypes.number,
  completed: PropTypes.bool,
  playing: PropTypes.bool,
  start: PropTypes.func,
  stop: PropTypes.func
};
registerStoppableMedia(SREPlayer);